import { AlertBox, Button } from '@/components/wm';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

const emptyAlert = { active: false, title: '', message: '', variant: '' };

// `onDataChange` is optional: it lets a caller observe the data the wizard owns,
// so the step list can depend on an answer given in an earlier step. Memoise it
// on the caller's side — it is an effect dependency.
//
// `onCancel`, when given, replaces leaving for the previous page on Cancel.
export function Wizard({ handleSubmit, initialData, onCancel, onDataChange, steps, successComponent = null, onClose }) {
    const
        [alert, setAlert] = useState(emptyAlert),
        [data, setData] = useState(initialData),
        [loading, setLoading] = useState(false),
        [showNext, setShowNext] = useState(false),
        [showSuccess, setShowSuccess] = useState(false),
        [step, setStep] = useState(0);

    const
        StepComponent = steps[step]?.Component,
        SuccessComponent = successComponent?.Component,
        totalSteps = steps.length;

    const
        cancelUrl = NavigationService.getCancelHref(),
        description = showSuccess ? successComponent.description : steps[step].description,
        stepPercentage = (step + 1) / totalSteps * 100,
        title = showSuccess ? successComponent.title : steps[step].title;

    useEffect(() => {
        onDataChange?.(data);
    }, [data, onDataChange]);

    const deactivateAlert = () => {
        setAlert(emptyAlert);
    };

    const handleAlert = (message, title, variant = '') => {
        deactivateAlert();

        setTimeout(() => {
            setAlert({ active: true, message, title, variant });
        }, 500);
    };

    const handleBack = () => {
        setStep(prev => Math.max(prev - 1, 0));
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            NavigationService.navigateTo(cancelUrl);
        }
    };

    const handleNext = () => {
        if (step === steps.length - 1) {
            submit();
        } else {
            setStep(prev => prev + 1);
        }
    };

    const onChangeField = field => eventOrValue => {
        let value;

        if (
            eventOrValue &&
            typeof eventOrValue === 'object' &&
            'target' in eventOrValue &&
            eventOrValue.target !== null
        ) {
            const target = eventOrValue.target;
            value = target.type === 'checkbox' ? target.checked : target.value;
        } else {
            value = eventOrValue;
        }

        setData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const submit = async () => {
        setLoading(true);

        try {
            await handleSubmit(data);
            handleAlert('Success', 'Step completed successfully', 'success');

            if (successComponent) {
                setShowSuccess(successComponent);
            } else if (onClose) {
                onClose();
            } else {
                NavigationService.navigateTo(cancelUrl);
            }
        } catch (err) {
            let displayMessage = 'Something went wrong';

            if (err instanceof Error || err.message) {
                displayMessage = err.message;
            } else if (err.responseText) {
                try {
                    const parsed = JSON.parse(err.responseText);
                    // Convert { "email": ["is invalid"] } to "email: is invalid"
                    displayMessage = Object.entries(parsed)
                        .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
                        .join('\n');
                } catch (e) {
                    displayMessage = err.statusText || 'Server Error';
                }
            }

            WiseMetering.layout.showTipper('error', displayMessage);
            // handleAlert(displayMessage, 'Error', 'destructive');
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    return (
        <div className="tw-relative tw-w-full tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
            <div className="!tw-bg-[#ffffff] tw-w-full tw-max-w-sm 2xl:tw-max-w-md tw-absolute tw-left-1/2
							tw-transform tw--translate-x-1/2 tw-top-4 tw-pr-14 sm:tw-pr-0 tw-rounded-lg">
                <AlertBox
                    active={alert.active}
                    animation={true}
                    animationProps={{ transition: { duration: 0.5 } }}
                    className={'!tw-mt-0'}
                    deactivateAlert={deactivateAlert}
                    duration={5000}
                    icon={alert.variant}
                    title={alert.title}
                    variant={alert.variant}
                >
                    {alert.message}
                </AlertBox>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    animate={{ x: 0, opacity: 1 }}
                    className="tw-flex tw-flex-col tw-min-h-[70vh] tw-h-full tw-flex-1 tw-w-full tw-px-10 2xl:tw-px-20 tw-py-8"
                    exit={{ x: '-100%', opacity: 0 }}
                    initial={{ x: '100%', opacity: 0 }}
                    key={step}
                    transition={{ duration: 0.5 }}
                >
                    {/* Header */}
                    <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                        <div className="tw-flex tw-flex-col">
                            <h2 className="tw-text-2xl tw-font-bold tw-text-secondary">{title}</h2>
                            <p className="tw-text-sm tw-text-gray-500">{description}</p>
                        </div>

                        {!showSuccess && (
                            <div className="tw-flex tw-items-center tw-h-full">
                                <div className="tw-relative tw-w-12 tw-h-12">
                                    <svg
                                        className="tw-w-full tw-h-full tw-transform tw-rotate-[-90deg]"
                                        viewBox="0 0 36 36"
                                    >
                                        <circle
                                            cx="18"
                                            cy="18"
                                            fill="none"
                                            r="16"
                                            stroke="#e5e7eb"
                                            strokeWidth="4"
                                        />
                                        <circle
                                            cx="18"
                                            cy="18"
                                            fill="none"
                                            r="16"
                                            stroke="#8BC53F"
                                            strokeDasharray="100"
                                            strokeDashoffset={100 - stepPercentage}
                                            strokeWidth="4"
                                        />
                                    </svg>
                                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-semibold tw-text-gray-700">
                                        {step + 1}/{totalSteps}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {showSuccess ? (
                        <SuccessComponent/>
                    ) : (
                        <>
                            <div className="tw-flex-grow tw-min-h-0 tw-pr-2">
                                {StepComponent && (
                                    <StepComponent
                                        data={data}
                                        onChangeField={onChangeField}
                                        onCompleted={state => {
                                            setShowNext(state);
                                        }}
                                    />
                                )}

                            </div>

                            {/* Navigation buttons */}
                            {/* <div className="tw-mt-auto tw-pt-4 tw-flex tw-justify-between tw-items-center"> */}
                            <div className="tw-mt-auto tw-pt-6 tw-flex tw-justify-between tw-items-center tw-flex-none tw-border-t tw-border-gray-100">
                                <Button
                                    className={`tw-min-w-20`}
                                    disabled={loading}
                                    onClick={step === 0 ? handleCancel : handleBack}
                                    size="sm"
                                    type="submit"
                                >
                                    {step === 0 ? i18next.t('ui.buttons.cancel') : i18next.t('ui.buttons.back')}
                                </Button>
                                <Button
                                    className="tw-min-w-20"
                                    disabled={!showNext}
                                    loading={loading}
                                    onClick={handleNext}
                                    size="sm"
                                    type="submit"
                                >
                                    {step + 1 === totalSteps ? i18next.t('ui.buttons.submit') : i18next.t('ui.buttons.next')}
                                </Button>
                            </div>
                        </>
                    )}
                    {loading && (
                        <div className="
                            tw-absolute tw-top-1/2 tw-left-1/2 tw-rounded-lg
                            tw-transform tw--translate-x-1/2 tw--translate-y-1/2
                            tw-max-w-[80vw] tw-max-h-[70vh] tw-w-full tw-h-full
                            tw-bg-white/70 tw-z-50 tw-flex tw-items-center tw-justify-center
                            " style={{ pointerEvents: 'all' }}>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
